import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';
import { FiFacebook, FiInstagram, FiTwitter } from 'react-icons/fi';
import colors from '../../styles/colors';

const FooterSection = styled.footer`
  background: ${colors.background.darker};
  padding: 80px 0 30px;
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 50px;
  padding-bottom: 50px;
  border-bottom: 1px solid ${colors.borderLight};

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const Brand = styled.div``;

const LogoWrapper = styled(RouterLink)`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 16px;
`;

const LogoImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid transparent;
  background: linear-gradient(${colors.background.darker}, ${colors.background.darker}) padding-box,
    ${colors.metallic.silver} border-box;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
`;

const Logo = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${colors.text.white};

  span {
    color: ${colors.accent.main};
  }
`;

const Tagline = styled.p`
  color: rgba(255,255,255,0.7);
  line-height: 1.7;
  margin-bottom: 24px;
  max-width: 280px;
`;

const Socials = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialLink = styled.a`
  width: 44px;
  height: 44px;
  background: ${colors.metallic.darkSteel};
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 2px 8px rgba(0, 0, 0, 0.3);

  svg {
    font-size: 1.2rem;
    color: ${colors.primary.light};
  }

  &:hover {
    background: ${colors.accent.gradient};
    border-color: ${colors.accent.dark};
    transform: translateY(-3px);
    box-shadow: 0 0 20px rgba(0, 102, 177, 0.4);

    svg {
      color: ${colors.text.white};
    }
  }
`;

const Column = styled.div``;

const ColumnTitle = styled.h4`
  font-size: 0.9rem;
  color: ${colors.primary.light};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 30px;
    height: 2px;
    background: ${colors.metallic.silver};
    border-radius: 1px;
  }
`;

const Link = styled(RouterLink)`
  display: block;
  color: rgba(255,255,255,0.7);
  margin-bottom: 12px;
  transition: all 0.3s ease;

  &:hover {
    color: ${colors.text.white};
    transform: translateX(5px);
  }
`;

const InfoText = styled.p`
  color: rgba(255,255,255,0.7);
  margin-bottom: 12px;
`;

const Bottom = styled.div`
  padding-top: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 600px) {
    justify-content: center;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: rgba(255,255,255,0.5);
  font-size: 0.9rem;
`;

const Credit = styled.p`
  color: rgba(255,255,255,0.35);
  font-size: 0.8rem;

  a {
    color: rgba(255,255,255,0.5);
    transition: color 0.3s ease;

    &:hover {
      color: white;
    }
  }
`;

export default function Footer() {
  return (
    <FooterSection>
      <Container>
        <Grid>
          <Brand>
            <LogoWrapper to="/">
              <LogoImage src="/images/logo.jpg" alt="The Honest Mechanic" />
              <Logo>The Honest <span>Mechanic</span></Logo>
            </LogoWrapper>
            <Tagline>
              Quality auto repair with integrity. No surprises, no upsells — just honest work at fair prices. BMW specialist.
            </Tagline>
            <Socials>
              <SocialLink href="#" aria-label="Facebook"><FiFacebook /></SocialLink>
              <SocialLink href="#" aria-label="Instagram"><FiInstagram /></SocialLink>
              <SocialLink href="#" aria-label="Twitter"><FiTwitter /></SocialLink>
            </Socials>
          </Brand>

          <Column>
            <ColumnTitle>Quick Links</ColumnTitle>
            <Link to="/">Home</Link>
            <Link to="/services">Services</Link>
            <Link to="/academy">HM Academy</Link>
            <Link to="/about">About Us</Link>
            <Link to="/contact">Contact</Link>
          </Column>

          <Column>
            <ColumnTitle>Specialty</ColumnTitle>
            <Link to="/bmw">BMW Expertise</Link>
            <Link to="/services">Oil Changes</Link>
            <Link to="/services">Brake Service</Link>
            <Link to="/services">Diagnostics</Link>
          </Column>

          <Column>
            <ColumnTitle>Info</ColumnTitle>
            <InfoText>Mon-Fri: 9am-5pm</InfoText>
            <InfoText>Sat-Sun: Closed</InfoText>
            <Link to="/about">Shop Policies</Link>
          </Column>
        </Grid>

        <Bottom>
          <Copyright>
            © {new Date().getFullYear()} The Honest Mechanic. All rights reserved.
          </Copyright>
          <Credit>
            Website created by <a href="https://forgewell.live" target="_blank" rel="noopener noreferrer">Forgewell</a>
          </Credit>
        </Bottom>
      </Container>
    </FooterSection>
  );
}
